import type { ThemeDefinition } from '../../core/domain'
import { STANDALONE_THEME_KEYS, THEME_NAME_KEYS } from '../../shared/themes'
import type { Translations } from './i18n/translations'
import { getSourceLink } from './sources'

export {
  RESTRICTED_THEME_FAMILIES,
  STANDALONE_THEME_KEYS,
  THEME_DEFINITIONS,
  THEME_NAME_KEYS,
  getThemeDefinition,
  isRestrictedThemeKey,
  themeDefinitionList
} from '../../shared/themes'

export function sequenceTarget(size: number): number {
  return (size * (size + 1)) / 2
}

export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(value)
}

export function stickerTitle(theme: ThemeDefinition, stickerNumber: number): string {
  const item = theme.items[stickerNumber - 1]
  if (!item) return `#${stickerNumber}`
  return item.name
}

export function stickerImage(theme: ThemeDefinition, stickerNumber: number): string | null {
  return theme.items[stickerNumber - 1]?.imageUrl ?? null
}

export function stickerFact(theme: ThemeDefinition, stickerNumber: number): string | null {
  return theme.items[stickerNumber - 1]?.fact ?? null
}

export function stickerSourceUrl(theme: ThemeDefinition, stickerNumber: number): string | null {
  const item = theme.items[stickerNumber - 1]
  if (!item?.sourceId) return null
  return getSourceLink(item.sourceId, item.sourcePath)
}

export function stickerValue(stickerNumber: number, albumSize: number): number {
  void albumSize
  return stickerNumber
}

export function daysUntil(dateString: string): number | null {
  if (!dateString) return null
  const target = new Date(dateString)
  if (isNaN(target.getTime())) return null
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

export function hasInvestmentThisMonth(stickers: Record<number, { investedAt: string }>): boolean {
  const now = new Date()
  const thisYear = now.getFullYear()
  const thisMonth = now.getMonth()
  return Object.values(stickers).some((sticker) => {
    const date = new Date(sticker.investedAt)
    return date.getFullYear() === thisYear && date.getMonth() === thisMonth
  })
}

export function investmentCountThisMonth(stickers: Record<number, { investedAt: string }>): number {
  const now = new Date()
  const thisYear = now.getFullYear()
  const thisMonth = now.getMonth()
  return Object.values(stickers).filter((sticker) => {
    const date = new Date(sticker.investedAt)
    return date.getFullYear() === thisYear && date.getMonth() === thisMonth
  }).length
}

export function themeDisplayName(theme: ThemeDefinition, translations: Translations): string {
  const nameKey = THEME_NAME_KEYS[theme.key]
  const translatedName = translations.themeNames[nameKey] ?? theme.name
  if (theme.key.startsWith('standard')) return translatedName
  if (STANDALONE_THEME_KEYS.has(theme.key)) return translatedName
  return `${translatedName} ${theme.size}`
}
